import React, { useState, useEffect, useCallback } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Label, Input, Spinner } from "reactstrap";
import BankAccountService from "../../../services/bank_account_service";
import { useTenant } from "../../../hooks/useTenant";
import { formatCurrency } from "../../Financial/DRE/utils/dreCalculations";
import { PAYMENT_METHODS, PAYMENT_METHOD_LABELS } from "../../../constants/financial";
import { FINANCIAL_TYPES } from "../../../constants/financial";

// Opções de pagamento derivadas da constante global
const PAYMENT_OPTIONS = Object.values(PAYMENT_METHODS).map(value => ({
    value,
    label: PAYMENT_METHOD_LABELS[value] || value
}));

/**
 * PaymentConfirmModal
 * Modal profissional de confirmação de baixa (recebimento ou pagamento).
 * Usado tanto em Contas a Receber quanto em Contas a Pagar.
 *
 * Props:
 * - isOpen: boolean
 * - toggle: fn
 * - record: o financialRecord sendo processado
 * - type: FINANCIAL_TYPES — define o título e labels
 * - onConfirm: fn(recordId, paymentData) — chamado ao confirmar
 * - processing: boolean
 */
const PaymentConfirmModal = ({ isOpen, toggle, record, type = FINANCIAL_TYPES.INCOME, onConfirm, processing }) => {
    const { idTenant } = useTenant();
    const isIncome = type === FINANCIAL_TYPES.INCOME;

    // State
    const [bankAccounts, setBankAccounts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);

    const [formData, setFormData] = useState({
        paymentDate: new Date().toISOString().split("T")[0],
        paymentMethod: "",
        idBankAccount: "",
        bankAccountName: "",
        amountPaid: "",
        observation: "",
    });

    // Carrega contas bancárias ao abrir
    useEffect(() => {
        if (!isOpen || !idTenant) return;
        setLoadingAccounts(true);
        BankAccountService.getBankAccounts(idTenant)
            .then(accounts => setBankAccounts(accounts.filter(a => a.status !== "INACTIVE")))
            .catch(err => console.error("[PaymentConfirmModal] Erro ao carregar contas:", err))
            .finally(() => setLoadingAccounts(false));
    }, [isOpen, idTenant]);

    // Preenche valores ao receber o record
    useEffect(() => {
        if (!record) return;
        setFormData({
            paymentDate: new Date().toISOString().split("T")[0],
            paymentMethod: record.paymentMethod || "",
            idBankAccount: record.idBankAccount || "",
            bankAccountName: record.bankAccountName || "",
            amountPaid: record.netAmount || record.grossAmount || "",
            observation: "",
        });
    }, [record]);

    const handleChange = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleBankAccountChange = useCallback((accountId) => {
        const acc = bankAccounts.find(a => a.id === accountId);
        setFormData(prev => ({
            ...prev,
            idBankAccount: accountId,
            bankAccountName: acc?.description || "",
        }));
    }, [bankAccounts]);

    const handleSubmit = useCallback(() => {
        if (!record || !formData.paymentDate || !formData.idBankAccount) return;
        onConfirm(record.id, {
            paymentDate: formData.paymentDate,
            paymentMethod: formData.paymentMethod || null,
            idBankAccount: formData.idBankAccount,
            bankAccountName: formData.bankAccountName,
            amountPaid: Number(formData.amountPaid) || record.netAmount || record.grossAmount || 0,
            observation: formData.observation || null,
        });
    }, [record, formData, onConfirm]);

    const isValid = formData.paymentDate && formData.idBankAccount;

    if (!record) return null;
    return (
        <Modal isOpen={isOpen} toggle={toggle} size="md" centered className="border-0 shadow">
            <ModalHeader toggle={toggle} className="border-bottom pb-3">
                <span className="fw-bold text-dark font-size-15">
                    <i className={`mdi ${isIncome ? "mdi-cash-plus" : "mdi-cash-minus"} me-2 ${isIncome ? "text-success" : "text-danger"}`} />
                    {isIncome ? "Confirmar Recebimento" : "Confirmar Pagamento"}
                </span>
            </ModalHeader>
            <ModalBody>
                {/* Info do registro */}
                <div
                    className="p-3 mb-3"
                    style={{
                        background: "#f8f9fa",
                        borderRadius: "6px",
                        border: "1px solid #e9ecef",
                    }}
                >
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <span className="fw-semibold" style={{ fontSize: "0.9rem" }}>
                                {record.description || "Sem descrição"}
                            </span>
                            {record.memberName && (
                                <small className="d-block text-muted">{record.memberName}</small>
                            )}
                            {record.entityName && !record.memberName && (
                                <small className="d-block text-muted">{record.entityName}</small>
                            )}
                        </div>
                        <div className="text-end">
                            <span className={`fw-bold ${isIncome ? "text-success" : "text-danger"}`} style={{ fontSize: "1.1rem" }}>
                                {formatCurrency(record.netAmount || record.grossAmount)}
                            </span>
                            {record.feeAmount > 0 && (
                                <small className="d-block text-muted">
                                    Bruto: {formatCurrency(record.grossAmount)} | Taxa: {formatCurrency(record.feeAmount)}
                                </small>
                            )}
                        </div>
                    </div>
                </div>

                <Row className="g-3">
                    {/* Data do pagamento */}
                    <Col md={6}>
                        <Label className="form-label small fw-bold">
                            Data do {isIncome ? "Recebimento" : "Pagamento"} *
                        </Label>
                        <Input
                            type="date"
                            bsSize="sm"
                            value={formData.paymentDate}
                            onChange={(e) => handleChange("paymentDate", e.target.value)}
                        />
                    </Col>

                    {/* Forma de pagamento */}
                    <Col md={6}>
                        <Label className="form-label small fw-bold">Forma de Pagamento</Label>
                        <Input
                            type="select"
                            bsSize="sm"
                            value={formData.paymentMethod}
                            onChange={(e) => handleChange("paymentMethod", e.target.value)}
                        >
                            <option value="">Selecione...</option>
                            {PAYMENT_OPTIONS.map(m => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </Input>
                    </Col>

                    {/* Conta Bancária */}
                    <Col md={6}>
                        <Label className="form-label small fw-bold">Conta Bancária *</Label>
                        {loadingAccounts ? (
                            <div className="py-1"><Spinner size="sm" /></div>
                        ) : (
                            <Input
                                type="select"
                                bsSize="sm"
                                value={formData.idBankAccount}
                                onChange={(e) => handleBankAccountChange(e.target.value)}
                                className={!formData.idBankAccount ? "border-warning" : ""}
                            >
                                <option value="">Selecione a conta...</option>
                                {bankAccounts.map(a => (
                                    <option key={a.id} value={a.id}>{a.description}</option>
                                ))}
                            </Input>
                        )}
                        {bankAccounts.length === 0 && !loadingAccounts && (
                            <small className="text-danger">
                                Nenhuma conta cadastrada. Cadastre em Configurações → Contas Bancárias.
                            </small>
                        )}
                    </Col>

                    {/* Valor efetivo */}
                    <Col md={6}>
                        <Label className="form-label small fw-bold">
                            Valor {isIncome ? "Recebido" : "Pago"}
                        </Label>
                        <Input
                            type="number"
                            bsSize="sm"
                            step="0.01"
                            min="0"
                            value={formData.amountPaid}
                            onChange={(e) => handleChange("amountPaid", e.target.value)}
                            placeholder="0,00"
                        />
                        <small className="text-muted" style={{ fontSize: "0.72rem" }}>
                            Altere apenas se diferente do valor original.
                        </small>
                    </Col>

                    {/* Observação */}
                    <Col md={12}>
                        <Label className="form-label small fw-bold">Observação</Label>
                        <Input
                            type="textarea"
                            bsSize="sm"
                            rows={2}
                            value={formData.observation}
                            onChange={(e) => handleChange("observation", e.target.value)}
                            placeholder="Nota interna sobre este pagamento (opcional)"
                        />
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter className="border-top pt-3">
                <Button color="light" className="text-muted" onClick={toggle} disabled={processing}>
                    Cancelar
                </Button>
                <Button
                    color={isIncome ? "success" : "primary"}
                    onClick={handleSubmit}
                    disabled={!isValid || processing}
                >
                    {processing ? (
                        <Spinner size="sm" className="me-1" />
                    ) : (
                        <i className="mdi mdi-check me-1" />
                    )}
                    {isIncome ? "Confirmar Recebimento" : "Confirmar Pagamento"}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default PaymentConfirmModal;
